// The school's themed portal, ported from Buy Flow.dc.html: 520px mobile
// shell, gradient school header, four-segment stepper, and the step router
// (1 requirements → 2 about-you → outcomes). Step 3 (price ladder) and the
// live quote call land with the public UAT quote endpoint; until then the
// application form renders fully and "Get my price" is disabled with
// "Quoting opens shortly" so nothing on production pretends to quote.
// ?demo_outcome=pending|review|declined|expired|interstitial|confirmation
// jumps straight to an outcome view (visual baselines; harmless in prod).
import { useEffect, useState } from 'react';
import { api } from '@/lib/api.js';
import Footer from '@/components/Footer.jsx';
import Wordmark from '@/components/Wordmark.jsx';
import Requirements from '@/routes/portal/Requirements.jsx';
import AboutYou from '@/routes/portal/AboutYou.jsx';
import Coverage from '@/routes/portal/Coverage.jsx';
import CheckoutGate from '@/routes/portal/CheckoutGate.jsx';
import Outcome, { FinalizingBlock } from '@/routes/portal/Outcome.jsx';
import UnclaimedPortal from '@/routes/UnclaimedPortal.jsx';

const STEP_LABELS = ["Your school's requirements", 'About you', 'Protect yourself further', 'Secure checkout'];
const OUTCOMES = ['pending', 'review', 'declined', 'expired', 'interstitial', 'confirmation'];

function monogram(name) {
  return String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
}

export default function SchoolPortal({ slug }) {
  const [school, setSchool] = useState(undefined); // undefined loading, null missing
  const [licensedStates, setLicensedStates] = useState(null);
  const [acksOn, setAcksOn] = useState(false); // true when the server's ack gate is ARMED
  const [ackGate, setAckGate] = useState(null); // {cfiOn} while the gate screen shows
  const [step, setStep] = useState(1);
  const [outcome, setOutcome] = useState(null);
  const [answers, setAnswers] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteError, setQuoteError] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [enteredUnclaimed, setEnteredUnclaimed] = useState(false);

  async function getPrice(a) {
    setAnswers(a); setQuoteError(null); setQuoting(true);
    try {
      const r = await api.post('/api/ctr/quote', { school_slug: slug, answers: a });
      if (r.outcome === 'quoted') { setQuote(r); setStep(3); }
      else setOutcome(r.outcome);
    } catch (e) {
      setQuoteError(e.code === 'quoting_unavailable'
        ? 'Quoting opens shortly. Your answers are good; try again soon.'
        : "We couldn't reach the carrier just now. Give it a minute and try again.");
    } finally { setQuoting(false); }
  }

  useEffect(() => {
    api.get(`/api/ctr/schools/by-slug/${encodeURIComponent(slug)}`)
      .then((r) => setSchool(r.school))
      .catch(() => setSchool(null));
    api.get('/api/ctr/config').then((r) => {
      setLicensedStates(r.licensed_states ?? null);
      setAcksOn(!!(r.checkout_acks && r.checkout_acks.mode === 'on'));
    }).catch(() => {});
    const demo = new URLSearchParams(window.location.search).get('demo_outcome');
    if (demo && OUTCOMES.includes(demo)) setOutcome(demo);
  }, [slug]);

  useEffect(() => {
    if (school?.accent_hex) document.documentElement.style.setProperty('--school-accent', school.accent_hex);
  }, [school]);

  if (school === undefined) return <div className="p-10 text-center text-sm text-ink-dim">Loading...</div>;
  if (school === null) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-5 text-center">
        <Wordmark className="text-xl text-primary" />
        <p className="mt-4 text-ink-soft">No school portal lives at <span className="font-mono">{slug}.clearedto.rent</span> yet.</p>
        <a href="https://clearedto.rent/signup" className="mt-4 rounded-card bg-primary px-4 py-2 text-sm font-semibold text-white">Claim this portal for your school</a>
      </div>
    );
  }

  // Unclaimed schools show the pre-claim page first; "Get my quote" walks
  // into the same steps (the typical requirements are the floor).
  if (school.status === 'unclaimed' && !enteredUnclaimed && !outcome) {
    return <UnclaimedPortal school={school} onGetQuote={() => setEnteredUnclaimed(true)} />;
  }

  const showStepper = !outcome;
  const effStep = Math.min(step, 4);

  return (
    <div className="mx-auto flex min-h-screen max-w-[520px] flex-col bg-paper shadow-shell">
      <header className="p-[20px_22px] text-white" style={{ background: 'linear-gradient(120deg, color-mix(in srgb, var(--school-accent) 72%, black), var(--school-accent))' }}>
        <div className="flex items-center gap-3">
          {school.logo_path
            ? <img src={school.logo_path} alt="" className="h-11 w-11 flex-none rounded-[11px] bg-white object-contain" />
            : <div className="flex h-11 w-11 flex-none items-center justify-center rounded-[11px] bg-white text-[17px] font-bold" style={{ color: 'var(--school-accent)' }}>{monogram(school.name)}</div>}
          <div className="min-w-0">
            <div className="text-[17px] font-bold">{school.name}</div>
            <div className="mt-0.5 text-[11.5px] opacity-75">{school.slug}.clearedto.rent{school.home_airport ? ` · ${school.home_airport}` : ''}</div>
          </div>
        </div>
        <div className="mt-3 text-[10.5px] leading-[1.5] opacity-75">Coverage by Global Aerospace · Arranged by NDEMNIFLY Aviation Insurance</div>
      </header>

      {school.watermark ? (
        <div className="bg-warn-bg px-3 py-1.5 text-center text-xs text-warn-fg">
          This portal is newly claimed and awaiting verification.
        </div>
      ) : null}

      {showStepper ? (
        <div className="bg-paper p-[16px_22px_4px]">
          {/* Slim progress bar with the step name below sm (mobile spec). */}
          <div className="sm:hidden">
            <div className="h-1 overflow-hidden rounded-pill bg-line">
              <div className="h-1 rounded-pill bg-primary transition-all" style={{ width: `${(effStep / STEP_LABELS.length) * 100}%` }} />
            </div>
            <div className="mt-1.5 text-[11px] font-bold tracking-[0.02em] text-ink">Step {effStep} of {STEP_LABELS.length} · {STEP_LABELS[effStep - 1]}</div>
          </div>
          <div className="hidden gap-1.5 sm:flex">
            {STEP_LABELS.map((label, i) => (
              <div key={label} className="flex-1">
                <div className={`h-1 rounded-pill ${i + 1 < effStep ? 'bg-ok-fg' : i + 1 === effStep ? 'bg-primary' : 'bg-line'}`} />
                <div className={`mt-1.5 text-[10px] font-bold leading-[1.3] tracking-[0.02em] ${i + 1 <= effStep ? 'text-ink' : 'text-ink-faint'}`}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <main className="flex flex-1 flex-col p-[18px_22px_40px]">
        {outcome === 'pending' ? (
          <div className="my-auto py-[30px]">
            <div className="mb-4 text-center text-sm text-ink-dim">Your required coverage is priced and locked.</div>
            <FinalizingBlock />
          </div>
        ) : outcome ? (
          <Outcome
            outcome={outcome}
            school={school}
            email={answers?.email || 'your email'}
            onRestart={() => { setOutcome(null); setStep(1); }}
            onContinuePayment={() => setOutcome('pending')}
            onBackToCoverage={() => { setOutcome(null); setStep(2); }}
          />
        ) : step === 1 ? (
          <Requirements school={school} onContinue={() => setStep(2)} />
        ) : step === 2 ? (
          <AboutYou
            school={school}
            licensedStates={licensedStates}
            quotingEnabled={!quoting}
            quoteError={quoteError}
            onSubmit={getPrice}
            onBack={() => setStep(1)}
          />
        ) : ackGate ? (
          <CheckoutGate
            quoteToken={quote?.quote_token}
            cfiExtension={!!(ackGate.cfiOn && quote?.cfi_extension)}
            onComplete={() => { setAckGate(null); setOutcome('pending'); }}
            onBack={() => setAckGate(null)}
          />
        ) : (
          <Coverage flow="school" school={school} quote={quote} answers={answers} electedTypes={school.aircraft_types || { SEL: true }}
            onCheckout={(selection) => {
              if (acksOn && quote?.quote_token) { setAckGate({ cfiOn: selection?.cfiOn }); return 'handled'; }
            }} />
        )}
      </main>
      <Footer />
    </div>
  );
}
